/**
 * 会话列表（任务 8d-2，两端共用）：GlobalState.conversations 只读渲染。
 * 数据写路径全在 conversationsState（快照+推送），本组件零状态逻辑；
 * 行渲染与行菜单在 ConversationRows（14.36b 抽出，归档页共用），
 * 行口径见 conversationRowModel.ts。
 *
 * - 归档（14.36）：归档会话从主列表分流到顶部「归档会话」入口行（有归档才显示，
 *   旧版 _archivePeekVisible 同口径；旧版的下拉过量才露出 + 可钉住两档形态不做，
 *   Web 上一个常驻条件行更直白）。入口行在虚拟列表**之外**作表头——不随列表滚动，
 *   行数再多也不占虚拟化预算；点击进归档页（14.36b，/archive）。归档会话收到
 *   新消息**不自动移出归档**（旧版同口径），未读汇总进入口行角标。
 * - 搜索/过滤（14.37）：搜索框 + 4 个筛选 chip 作表头常驻，**两端同一套**
 *   （旧版只有桌面 pane 有内联框，移动端顶部入口跳的是「搜索加人」服务端搜索页——
 *   那条路新版也在，通讯录 Tab，14.19b）。全程本地零 HTTP，口径见
 *   conversationFilterModel.ts。过滤态（chip≠全部 或 有查询串）下归档入口行隐藏
 *   ——它不是搜索结果，留着会让「结果条数」对不上。
 * - 搜索态是组件本地 state：移动端开会话会卸载 HomePage，返回即清空（本就是
 *   一次性动作，不进 store 也不进 URL）；桌面双栏开会话不卸载，搜索态保留。
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import { useGlobal } from '~/store/useGlobal';
import { appRouter } from '~/nav/appRouter';
import { openArchive, openContactApplies } from '~/nav/actions';
import { archiveEntryView, partitionArchived } from './archiveModel';
import {
  CONVERSATION_FILTERS,
  filterConversations,
  isFiltering,
  normalizeQuery,
  type ConversationFilterId,
} from './conversationFilterModel';
import { ConversationRows } from './ConversationRows';
import { applyEntryViews, type ApplyEntryView } from './applyEntryModel';
import { markOnce, PERF_MARKS } from '~/perf/marks';
import { IconGroupAdd, IconPersonAdd } from '~/ui/icons';
import styles from './conversationList.module.scss';

export function ConversationList() {
  const rows = useGlobal((s) => s.conversations.rows);
  const archive = useGlobal((s) => s.archive);
  // 申请红点（21ag）：会话列表顶部的「好友申请/群通知」两条虚拟会话，数据源与
  // 通讯录红点同一份（contacts.points）；登录即拉一次，见 contactsState
  const points = useGlobal((s) => s.contacts.points);
  const isMobile = useGlobal((s) => s.layout.isMobile);
  const [filter, setFilter] = useState<ConversationFilterId>('all');
  const [rawQuery, setRawQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useSearchHotkeys(!isMobile, inputRef);

  // 下拉刷新（21ag-3，仅移动端）：与列表头「刷新」钮同一条路径——让 Worker
  // 重新对账服务端第 1 页，再重拉一次本地快照兜底（conversationsState.refresh）。
  // 桌面不接：有鼠标就有刷新钮，下拉手势只会误触。
  //
  // 两处为首屏预算做的让步（只剩几十字节，一个模块边界都是奢侈）：
  // ① ref 挂在本组件根节点上，真正滚动的那个元素由手势层自己找（pullRefresh.ts）——
  //    把 ref 从 FixedList 一路透传上来要在首屏多两个 props；
  // ② 这里内联动态 import 而不用 `~/ui/usePullRefresh`——那个 hook 同时被懒加载的
  //    「我的」Tab 用，首屏一旦也引它，打包器就得把它提到首屏 chunk。
  const listScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = listScrollRef.current;
    if (!isMobile || !el) return;
    let off: (() => void) | undefined;
    let dead = false;
    void import('~/ui/pullRefresh').then(
      (m) => {
        if (!dead) off = m.attachConversationsPullRefresh(el);
      },
      () => undefined, // chunk 取不到（断网）：没有下拉刷新而已，列表照常
    );
    return () => {
      dead = true;
      off?.();
    };
  }, [isMobile]);

  // null = 快照未回包（毫秒级 IDB 读），留白不闪「暂无会话」
  if (rows === null) return <div className={styles.blank} data-testid="conv-loading" />;
  // 「可交互」打点（20a）：留白态不算，拿到快照（含空列表）才算首屏主内容已决定
  markOnce(PERF_MARKS.interactive);
  // 归档分流（14.36）：archive 未加载/无归档时 visible 是原引用，零开销
  const { visible, archived } = partitionArchived(rows, archive);
  const query = normalizeQuery(rawQuery);
  const filtering = isFiltering(filter, query);
  const shown = filterConversations(visible, filter, query);
  const entry = !filtering && archived.length > 0 ? archiveEntryView(archived) : null;
  const applies = applyEntryViews(points, filter, query);
  // 一条会话都没有**且**没有待处理申请才是真空列表——新号的第一条「会话」
  // 往往就是别人发来的好友申请（21ag），那时不能整页只写「暂无会话」
  if (rows.length === 0 && applies.length === 0) {
    return (
      <div className={styles.empty} data-testid="conv-empty">
        暂无会话
      </div>
    );
  }
  return (
    <div className={styles.wrap} ref={listScrollRef}>
      <div className={styles.searchBar}>
        <input
          ref={inputRef}
          className={styles.searchInput}
          data-testid="conv-search"
          type="search"
          /* 不给密码管理器当「用户名」填（21d-3）：加密密钥弹层一开，
             Chrome 会把页面里最近的文本框当用户名一起回填 */
          name="conv-search"
          autocomplete="off"
          data-1p-ignore
          data-lpignore="true"
          data-form-type="other"
          /* 桌面才提快捷键；移动端没有物理键盘，提示反成噪声 */
          placeholder={isMobile ? '搜索会话' : '搜索会话（Ctrl/⌘ + K）'}
          value={rawQuery}
          onInput={(e) => setRawQuery(e.currentTarget.value)}
          onKeyDown={(e) => {
            // Esc：有内容先清空，空了再失焦（两端同；桌面常用、移动端外接键盘也有）
            if (e.key !== 'Escape') return;
            e.preventDefault();
            if (rawQuery !== '') setRawQuery('');
            else e.currentTarget.blur();
          }}
        />
      </div>
      <div className={styles.chips} data-testid="conv-filters">
        {CONVERSATION_FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={f.id === filter ? styles.chipActive : styles.chip}
            data-testid="conv-filter-chip"
            data-filter={f.id}
            data-active={f.id === filter ? 'true' : undefined}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>
      {/* 申请会话两条（21ag；旧版 chat_list_screen 注入的虚拟系统会话同位：
          在真会话之上、归档入口之上）。点进去是申请处理页（14.20） */}
      {applies.map((a) => (
        <ApplyEntryRow key={a.id} view={a} onClick={() => openContactApplies(appRouter(), a.id)} />
      ))}
      {entry !== null && (
        <ArchiveEntryRow view={entry} onClick={() => openArchive(appRouter())} />
      )}
      <ConversationRows
        rows={shown}
        emptyText={rows.length === 0 ? '暂无会话' : filtering ? '未找到相关会话' : '会话都在归档里'}
        emptyTestId={
          rows.length === 0 ? 'conv-empty' : filtering ? 'conv-no-match' : 'conv-all-archived'
        }
      />
    </div>
  );
}

/**
 * 桌面快捷键（旧版 CallbackShortcuts：Ctrl 与 Cmd 双绑，K 与 F 都聚焦搜索）。
 * Cmd+F 抢的是浏览器查找——这正是旧版桌面壳的行为，也只有「搜当前会话列表」
 * 对用户有意义（页面里本就没有可查找的长文本）。移动布局不挂监听。
 */
function useSearchHotkeys(enabled: boolean, ref: { current: HTMLInputElement | null }): void {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const key = e.key.toLowerCase();
      if (key !== 'k' && key !== 'f') return;
      const input = ref.current;
      if (input === null) return;
      e.preventDefault();
      input.focus();
      input.select();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, ref]);
}

/** 申请会话行（21ag）：形态与归档入口行同构——图标块 + 标题 + 预览 + 未读角标 */
function ApplyEntryRow({ view, onClick }: { view: ApplyEntryView; onClick: () => void }) {
  return (
    <button
      type="button"
      className={`${styles.row} ${styles.archiveRow}`}
      data-testid={`conv-apply-entry-${view.id}`}
      data-count={view.count}
      onClick={onClick}
    >
      <span
        className={`${styles.avatar} ${styles.applyIcon}`}
        data-kind={view.id}
        aria-hidden="true"
      >
        {view.id === 'friend' ? (
          <IconPersonAdd class={styles.applyGlyph} />
        ) : (
          <IconGroupAdd class={styles.applyGlyph} />
        )}
      </span>
      <span className={styles.main}>
        <span className={styles.topLine}>
          <span className={styles.name}>{view.title}</span>
        </span>
        <span className={styles.bottomLine}>
          <span className={styles.preview}>{view.preview}</span>
          <span className={styles.badge} data-testid={`conv-apply-badge-${view.id}`}>
            {view.count > 99 ? '99+' : view.count}
          </span>
        </span>
      </span>
    </button>
  );
}

function ArchiveEntryRow(props: {
  view: ReturnType<typeof archiveEntryView>;
  onClick: () => void;
}) {
  const { view } = props;
  return (
    <button
      type="button"
      className={`${styles.row} ${styles.archiveRow}`}
      data-testid="conv-archive-entry"
      data-count={view.count}
      onClick={props.onClick}
    >
      <span className={`${styles.avatar} ${styles.archiveIcon}`} aria-hidden="true">
        <span className={styles.avatarChar}>⌂</span>
      </span>
      <span className={styles.main}>
        <span className={styles.topLine}>
          <span className={styles.name}>归档会话</span>
          <span className={styles.time}>{view.count} 个会话</span>
        </span>
        <span className={styles.bottomLine}>
          <span className={styles.preview}>{view.preview}</span>
          {view.badge && (
            <span
              className={view.badge.muted ? styles.badgeMuted : styles.badge}
              data-testid="conv-archive-badge"
              data-muted={view.badge.muted ? 'true' : undefined}
            >
              {view.badge.text}
            </span>
          )}
        </span>
      </span>
    </button>
  );
}
